import { MemoryRouter, Navigate, Route, Routes } from 'react-router-dom';

import { PostHogProvider } from 'posthog-js/react';
import { SWRConfig } from 'swr';

import Progress from '@alexandria/components/Progress';
import Login from '@auth/Login';
import Signup from '@auth/Signup';
import POSTHOG_API_KEY from '@constants/PosthogApiKey';
import '@css/global/Global.css';

import AuthProvider from 'components/AuthProvider';
import ErrorBoundary from 'components/ErrorBoundary';
import RequireAuth from 'components/RequireAuth';
import ServiceProvider from 'components/ServiceProvider';
import SidebarStateProvider from 'components/SidebarStateContext';
import SidebarViewProvider from 'components/SidebarViewContext';
import { DrumkitPlatform } from 'contexts/sidebarStateContext';
import { ThemeProvider } from 'contexts/themeContext';
import { fetcher } from 'utils/fetcher';

import { OfficeContextConsumer, OfficeContextProvider } from './OfficeContext';
import { PortalContextProvider } from './PortalContext';
import SidebarOutlookWrapper from './SidebarWrapper';

/* global require */

export interface AppProps {
  title: string;
}

function App({ title }: AppProps) {
  return (
    <OfficeContextProvider>
      <PortalContextProvider>
        <OfficeContextConsumer>
          {({
            isInitialized: isOfficeInitialized,
            isLoading: isOfficeLoading,
            inboxEmailAddress,
            subject,
            threadId,
            threadItemId,
            isOutlookReply,
            isSharedMailboxEmail,
            threadReferences,
            inReplyTo,
            state,
          }) => {
            // Extract thread participants from Office context
            // In compose mode, to/cc are resolved async in OfficeContext
            // In read mode, they are arrays of EmailAddressDetails
            const isComposeMode = !Array.isArray(state?.mailbox?.item?.to);
            const threadFrom = state?.mailbox?.item?.from?.emailAddress || '';
            const threadTo = state?.mailbox?.item?.to
              ? state.mailbox.item.to.map((t) => t.emailAddress).filter(Boolean)
              : [];
            const threadCc = state?.mailbox?.item?.cc
              ? state.mailbox.item.cc.map((c) => c.emailAddress).filter(Boolean)
              : [];

            // In compose mode, itemId is the draft ID which cannot be used for
            // Graph API reply operations. Clear it to prevent invalid API calls.
            // Reply-in-thread will be disabled when threadItemId is empty.
            const effectiveThreadItemId = isComposeMode ? '' : threadItemId;

            const effectiveThreadReferences = isComposeMode
              ? ''
              : threadReferences;
            const effectiveInReplyTo = isComposeMode ? [] : inReplyTo;

            return (
              <>
                <style
                  dangerouslySetInnerHTML={{
                    __html:
                      "@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600&display=swap');",
                  }}
                />
                <SWRConfig
                  value={{
                    revalidateOnFocus: false,
                    fetcher,
                  }}
                >
                  <>
                    {!isOfficeLoading && !isOfficeInitialized && (
                      <Progress
                        title={title}
                        // eslint-disable-next-line @typescript-eslint/no-require-imports
                        logo={require('../../assets/drumkit-logo.png')}
                        message='Please sideload your add-in to see app body.'
                      />
                    )}
                    {isOfficeInitialized && (
                      <MemoryRouter>
                        <Routes>
                          <Route
                            path='/'
                            element={
                              <ErrorBoundary>
                                <PostHogProvider
                                  apiKey={POSTHOG_API_KEY}
                                  options={{
                                    api_host: 'https://us.i.posthog.com',
                                    person_profiles: 'always',
                                    capture_pageview: false,
                                  }}
                                >
                                  <ThemeProvider>
                                    <ServiceProvider>
                                      <SidebarStateProvider
                                        inboxEmailAddress={inboxEmailAddress}
                                        drumkitPlatform={
                                          DrumkitPlatform.Outlook
                                        }
                                        subject={subject}
                                        threadId={threadId}
                                        threadItemId={effectiveThreadItemId}
                                        isOutlookReply={isOutlookReply}
                                        isSharedMailboxEmail={
                                          isSharedMailboxEmail
                                        }
                                        threadFrom={threadFrom}
                                        threadTo={threadTo}
                                        threadCc={threadCc}
                                        threadReferences={
                                          effectiveThreadReferences
                                        }
                                        inReplyTo={effectiveInReplyTo}
                                      >
                                        <AuthProvider>
                                          <RequireAuth>
                                            <SidebarViewProvider>
                                              <SidebarOutlookWrapper />
                                            </SidebarViewProvider>
                                          </RequireAuth>
                                        </AuthProvider>
                                      </SidebarStateProvider>
                                    </ServiceProvider>
                                  </ThemeProvider>
                                </PostHogProvider>
                              </ErrorBoundary>
                            }
                          />
                          <Route path='/signup' element={<Signup />} />
                          <Route path='/login' element={<Login />} />
                          <Route path='/home' element={<Navigate to={'/'} />} />
                        </Routes>
                      </MemoryRouter>
                    )}
                  </>
                </SWRConfig>
              </>
            );
          }}
        </OfficeContextConsumer>
      </PortalContextProvider>
    </OfficeContextProvider>
  );
}

export default App;
